import React, { useCallback } from 'react';
import type { GoodsDiscountGroupItem } from 'shared/src/api';
import {
  Checkbox,
  ColorValues,
  Flex,
  IconChevronRight,
  Typography,
  usePopup,
} from '@banhala/ui-kit';
import { bridge, isNil, isNotNil, localizeNumber } from 'shared/src/utils';
import { useLogClickCheckBox } from '../hooks/useLogClickCheckBox';
import { BrandIconAblyPay } from './BrandIconAblyPay';
import { useGoodsDetail } from '../../../queries/useGoodsDetail';
import { useLogGoodsAnalyticsEvent } from '../../../hooks/useLogGoodsAnalyticsEvent';
import { useGoodsSno } from '../../../hooks/useGoodsSno';
import { useIsAnonymousUser } from 'shared/src/hooks/useIsAnonymousUser';
import { useOpenDeeplink, useSafeTimeout } from 'shared/src/hooks';

// const KEY_ABLY_PAY_REGISTER_CONFIRM_SHOWN = 'ablyPayRegisterConfirmShown';

export const AblyPayItemView: React.FC<{
  item: GoodsDiscountGroupItem;
  checked: GoodsDiscountGroupItem[];
  onChangeChecked: (checked: GoodsDiscountGroupItem[]) => void;
  salePrice?: number;
}> = ({ checked, item, onChangeChecked, salePrice }) => {
  const isChecked =
    checked.findIndex(checkItem => `${checkItem.key}` === `${item.key}`) !== -1;
  const isAnonymousUser = useIsAnonymousUser();
  const { data: goodsDetail } = useGoodsDetail();
  const logClickCheckBox = useLogClickCheckBox(item);
  const logEvent = useLogGoodsAnalyticsEvent();
  const goodsSno = useGoodsSno();
  const { show: showPopup } = usePopup();
  const { openDeeplink } = useOpenDeeplink();
  const setSafeTimeout = useSafeTimeout();

  const onPressRegeditNow = useCallback(() => {
    if (isNil(goodsDetail)) return;
    if (isAnonymousUser) {
      bridge.assertLogin({ redirect: `/goods/${goodsSno}` });
      setSafeTimeout(() => {
        bridge.toast('로그인 후 이용할 수 있습니다');
      }, 200);
      return;
    }
    logEvent({
      name: 'CLICK_ABLYPAY_BENEFIT',
      analytics: {
        IS_REGISTERED: goodsDetail.ably_pay?.is_registered ? '1' : '0',
        IS_REGISTERED_ACCOUNT: goodsDetail.ably_pay?.is_registered_account
          ? '1'
          : '0',
        TOTAL_PAYMENT_AMOUNT: salePrice ?? 0,
      },
    });
    if (isNotNil(goodsDetail.ably_pay)) {
      openDeeplink(goodsDetail.ably_pay.deeplink);
    }
  }, [
    goodsDetail,
    isAnonymousUser,
    logEvent,
    goodsSno,
    openDeeplink,
    salePrice,
    setSafeTimeout,
  ]);

  const onPressItem = useCallback(async () => {
    if (isChecked) {
      logClickCheckBox(false);
      onChangeChecked(checked.filter(check => check.key !== item.key));
    } else {
      logClickCheckBox(true);
      onChangeChecked(checked.concat([item]));

      if (!goodsDetail?.ably_pay?.is_registered_account) {
        // const hasShown = await localStorageBridge.getBoolean(
        //   KEY_ABLY_PAY_REGISTER_CONFIRM_SHOWN,
        // );
        // if (!hasShown) {
        //   localStorageBridge.putBoolean(
        //     KEY_ABLY_PAY_REGISTER_CONFIRM_SHOWN,
        //     true,
        //   );
        const result = await showPopup({
          title: `에이블리페이 등록하고 첫 결제 페이백 ${localizeNumber(item.amount)}원 받으시겠어요?`,
          confirm: '페이백 받기',
          cancel: '취소',
        });
        if (result.type === 'confirm') {
          onPressRegeditNow();
        }
        // }
      } else {
        onPressRegeditNow();
      }
    }
  }, [
    isChecked,
    logClickCheckBox,
    onChangeChecked,
    checked,
    item,
    goodsDetail,
    onPressRegeditNow,
    showPopup,
  ]);

  return (
    <Flex>
      <Flex.Row
        alignItems={'center'}
        padding={{
          vertical: 4,
        }}
      >
        <Flex.Row
          flex={1}
          alignItems={'center'}
          gap={8}
          onPress={onPressItem}
          padding={{ right: 8 }}
        >
          <Checkbox value={isChecked} onPress={onPressItem}></Checkbox>
          <Flex.Row flex={1} alignItems={'center'} gap={4}>
            <BrandIconAblyPay />
            <Typography type={'body3'} numberOfLines={1}>
              첫 결제 페이백
            </Typography>
          </Flex.Row>
        </Flex.Row>
        <Typography
          type={'subtitle3'}
          color={isChecked ? 'content_primary' : 'content_tertiary'}
        >
          -{localizeNumber(item.amount)}원
        </Typography>
      </Flex.Row>
      {!goodsDetail?.ably_pay?.is_registered_account && (
        <Flex padding={{ vertical: 4 }}>
          <Flex.Row
            alignItems={'center'}
            justifyContent={'center'}
            padding={{ vertical: 8 }}
            backgroundColorValue={ColorValues.lilac10}
            borderRadius={4}
            gap={4}
            onPress={onPressRegeditNow}
          >
            <Typography type={'body3'} rawColor={ColorValues.lilac40}>
              지금 등록하고 페이백 혜택받기
            </Typography>
            <IconChevronRight size={16} fill={ColorValues.lilac40} />
          </Flex.Row>
        </Flex>
      )}
    </Flex>
  );
};
